import axios, { isAxiosError } from 'axios';
// TODO: Evaluate whether we want to keep using zod
import { z } from 'zod';

import Routes from 'src/config/routes';
import type { CommandStatus } from 'src/hooks/command';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';
import { exceptionFromResponse } from 'src/utils/api';

import { getConfigValues } from '../config';

export interface PostListingSuccessfulResponsePayload {
  // the payload is richer than this
  // but for some reason we're only invested in knowing these fields to validate that posting succeeded
  // full payload is found here: https://github.com/SEEK-Jobs/sbiz-seek-business/blob/master/Api/Seek.Business.Api/Models/Listing.cs#L119
  autoRenew: boolean;
  creditRemaining: number;
  expiryDate: string;
  productCode: string;
}

export type PostListingResponse =
  | { kind: 'success'; data: PostListingSuccessfulResponsePayload }
  | { kind: 'error'; message: string; errorCode: string };

export const legacyApi = axios.create({
  baseURL: getConfigValues().SERVER_BASE_URL,
});

export const getAccount = () =>
  legacyApi
    .get('/api/account')
    .then((response) => ({ ...response.data, status: response.status }))
    // This catch method eats the error and causes this functions promise to resolve
    // rather than reject. If we would like to propagate this error then it should be
    // rethrown
    .catch((error) => ({ ...error.response }));

type SignupNewsletterResult =
  | { kind: 'unexpected-server-error' }
  | { kind: 'validation-error' }
  | { kind: 'success' }
  | { kind: 'unexpected-server-error' };

export function signupNewsletter(
  formData: FormData,
): Promise<SignupNewsletterResult> {
  const Response = z.union([
    z.object({
      message: z.literal('Success'),
      result: z.literal(true),
    }),
    // When this API fails, it'll return a 200 with error details.
    // E.g. submit a "+" in an email
    z.object({
      message: z.literal('validation-error').optional(),
      result: z.literal(false),
    }),
  ]);

  return legacyApi
    .post('/business-insights-sign-up', formData)
    .then((response) => {
      const parse = Response.safeParse(response.data);

      if (!parse.success) {
        consoleErrorWrapper(
          ConsoleErrorId.SignupNewsletterRequestParseFailure,
          { response },
        );
        return { kind: 'unexpected-server-error' as const };
      }

      // Is the error a known validation error?
      if (parse.data.message === 'validation-error') {
        consoleErrorWrapper(
          ConsoleErrorId.SignupNewsletterRequestServerValidation,
          { response },
        );
        return { kind: 'validation-error' as const };
      }
      // Is the error unexpected?
      if (!parse.data.result) {
        consoleErrorWrapper(
          ConsoleErrorId.SignupNewsletterRequestUnknownFailure,
          { response },
        );
        return { kind: 'unexpected-server-error' as const };
      }

      return { kind: 'success' as const };
    })
    .catch((error) => {
      consoleErrorWrapper(ConsoleErrorId.SignupNewsletterNetworkRequest, {
        error,
      });
      return { kind: 'unexpected-server-error' as const };
    });
}

// TODO: remove
export const getAccountListings = (clientId: string) =>
  legacyApi
    .get(`/api/account/${clientId}/listings`, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => response.data);

// TODO: remove
export const getListingStats = (listingId: string) =>
  legacyApi
    .get(`/api/listing/${listingId}/stats`, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => ({ ...response.data }))
    .catch((error) => ({ code: error.response.request.status }));

export const getSecureAsset = (assetKey: string) =>
  legacyApi
    .get(`/asset?key=${assetKey}`, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => response.data as string)
    .catch(() => Routes.ERROR_PAGE);

export const postListing = (listingId: string): Promise<PostListingResponse> =>
  legacyApi
    .post(`/api/listing/${listingId}/post`, undefined, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then(
      (response): PostListingResponse => ({
        kind: 'success',
        data: response.data,
      }),
    )
    .catch((error): PostListingResponse => {
      if (isAxiosError(error) && error.response?.data?.errorCode) {
        return {
          kind: 'error',
          message: error.response.data.message,
          errorCode: error.response?.data?.errorCode ?? 'generic_error',
        };
      }
      return {
        kind: 'error',
        message: `An error occurred while posting your listing. ${(error as Error).message}`,
        errorCode: 'generic_error',
      };
    });

export const expireListing = (
  listingId: string,
  reason: string,
  comments: string,
) =>
  legacyApi
    .post(
      `/api/listing/${listingId}/expire`,
      { reason, comments },
      {
        headers: {
          Accept: 'application/json',
        },
      },
    )
    .then((response) => ({ ...response.data }))
    .catch((error) => {
      throw exceptionFromResponse(error.response);
    });

export const subscribeUser = (userId: string) =>
  axios
    .post(`${getConfigValues().SUBSCRIPTIONS_API_URL}/user/${userId}/subscribe`)
    .catch((error) => error);

export const unsubscribeUser = (
  setStatus: (status: CommandStatus) => void,
  userId: string,
): void => {
  axios
    .post(
      `${getConfigValues().SUBSCRIPTIONS_API_URL}/user/${userId}/unsubscribe`,
    )
    .then(() => setStatus && setStatus('SUCCESS'))
    .catch((error) => (setStatus ? setStatus('FAIL') : error));
};

export const sendRecommendationFeedback = (
  token: string,
  message: string,
  listingId: string,
) =>
  axios
    .post(
      `${
        getConfigValues().INFORMANT_URL
      }/user/unverified/recommendationPreferences/feedback`,
      {
        message,
        listingId,
      },
      {
        headers: {
          'x-token': token,
        },
      },
    )
    .catch((error) => error);
